/**
 * InchBug WebMCP tools.
 *
 * Registers custom tools with the browser's model context (WebMCP) so AI
 * shopping agents in the shopper's browser can personalize labels and get
 * quiz-backed recommendations — the two things Shopify's built-in storefront
 * tools (search_catalog, update_cart, …) cannot do, because update_cart has
 * no line item property support.
 *
 * Registration surface: document.modelContext (navigator.modelContext is the
 * deprecated origin-trial location). Only registerTool() is used — never a
 * context-replacing call — so Shopify's built-in tools are left untouched.
 * On browsers without WebMCP everything below is a silent no-op except the
 * window.inchbugWebMCP console harness, which always exists for testing.
 *
 * Page context is read from the #webmcp-data JSON blob rendered by
 * snippets/webmcp-tools.liquid. Quiz data is fetched lazily through the
 * Section Rendering API (sections/webmcp-quiz-data.liquid) on the first
 * recommendation call, so regular page loads carry no quiz payload.
 */

const TOOL_TIMEOUT_MS = 20000;
const QUIZ_DATA_SECTION = 'webmcp-quiz-data';

/** @type {Map<string, {name: string, description: string, inputSchema: object, annotations?: object, execute: Function}>} */
const localTools = new Map();

const readPageData = () => {
  const element = document.getElementById('webmcp-data');
  if (!element) return null;

  try {
    return JSON.parse(element.textContent);
  } catch (error) {
    console.error('[webmcp] invalid page data', error);
    return null;
  }
};

const pageData = readPageData();

const track = (tool, input) => {
  document.dispatchEvent(
    new CustomEvent('inchbug:webmcp-tool-call', {
      detail: { tool, input },
      bubbles: true,
    })
  );
};

const isVisible = (element) => element.getClientRects().length > 0;

/**
 * Locates the mounted Vue customizer app the shopper is looking at. Several
 * customizers can be mounted at once (PDP plus cart drawer gift/upsell
 * panels), so visible mounts win, and among visible ones the PDP mount (not
 * inside a dialog) wins over drawer panels.
 *
 * @returns {{proxy: object, sectionId: string} | null}
 */
const getCustomizer = () => {
  const mounts = [...document.querySelectorAll('[data-customizer-app][data-section-id]')];
  if (!mounts.length) return null;

  const score = (element) => {
    let value = 0;
    if (element.hasAttribute('data-vue-ready')) value += 4;
    if (isVisible(element)) value += 2;
    if (!element.closest('dialog')) value += 1;
    return value;
  };

  const mount = mounts.sort((a, b) => score(b) - score(a))[0];
  const sectionId = mount.dataset.sectionId;
  const app = window.customizerApp?.[sectionId];
  // app.vm is set by vue-script.liquid at mount time. app._instance is
  // devtools-only and null in production Vue builds; the container vnode is
  // the last-resort path for apps mounted before app.vm existed.
  const proxy = app?.vm ?? app?._instance?.proxy ?? mount._vnode?.component?.proxy;
  if (!proxy) return null;

  return { proxy, sectionId };
};

const noCustomizerMessage = () => {
  if (pageData?.context?.productUsesCustomizer || document.querySelector('[data-customizer-app]')) {
    return JSON.stringify({
      error: 'customizer_not_ready',
      message: 'This product is personalizable but its customizer has not finished loading. Wait a moment and call this tool again.',
    });
  }

  const productHint = pageData?.context?.productHandle
    ? `The current product (${pageData.context.productHandle}) does not use the label customizer.`
    : 'No personalizable product is open on this page.';

  return JSON.stringify({
    error: 'no_customizer_on_page',
    message: `${productHint} Personalized InchBug products use the "vue-customizer" product template. Navigate to a personalizable product page (for example via search_catalog and get_product), then call this tool again.`,
  });
};

/**
 * Case-insensitive option lookup by name or handle/key.
 *
 * @param {Array<object> | undefined} list
 * @param {string} value
 * @returns {object | undefined}
 */
const findOption = (list, value) => {
  if (!list || !value) return undefined;
  const wanted = String(value).trim().toLowerCase();
  return list.find((option) =>
    [option.name, option.handle, option.key].some((candidate) => String(candidate ?? '').toLowerCase() === wanted)
  );
};

const optionNames = (list) => (list ?? []).map((option) => option.name).filter(Boolean);

// The app's own icon list uses the single-character keys the renderer
// expects; the raw data list on the root uses slug keys.
const orbitIconList = (proxy) =>
  typeof proxy.getOrbitIconOptions === 'function' ? proxy.getOrbitIconOptions() : proxy.orbitIconOptions;

const allowsLine2 = (proxy) => {
  if (typeof proxy.isTextLine === 'function') return proxy.isTextLine('Line_2');
  return (typeof proxy.maxTextLines === 'function' ? proxy.maxTextLines() : 1) >= 2;
};

/**
 * The customizer's current personalization state.
 *
 * @param {object} proxy
 * @returns {object}
 */
const currentState = (proxy) => ({
  line_1: proxy.texts?.Line_1 ?? '',
  line_2: proxy.texts?.Line_2 ?? '',
  letter: proxy.texts?.Letter ?? '',
  font: proxy.selection?.font?.name ?? null,
  color: proxy.selection?.color?.name ?? null,
  color_alt: proxy.selection?.color_alt?.name ?? null,
  icon_set: proxy.selection?.set?.name ?? null,
  quantity: proxy.quantity ?? 1,
  is_complete: Boolean(proxy.isCustomizationComplete),
});

/**
 * Snapshot of the mounted customizer's constraints and current state.
 *
 * @param {object} proxy
 * @returns {object}
 */
const describeCustomizer = (proxy) => {
  const maxLines = typeof proxy.maxTextLines === 'function' ? proxy.maxTextLines() : 1;

  return {
    product: {
      title: proxy.product?.title,
      handle: proxy.product?.handle,
    },
    label: {
      type: proxy.label?.type,
      size: proxy.label?.size,
      max_lines: maxLines,
      max_characters_per_line: proxy.label?.textLength ?? 12,
      line_2_allowed: allowsLine2(proxy),
    },
    options: {
      fonts: optionNames(proxy.options?.fonts),
      colors: optionNames(proxy.options?.colors),
      color_alts: optionNames(proxy.options?.color_alts),
      icon_sets: optionNames(proxy.options?.sets),
      orbit_icons: optionNames(orbitIconList(proxy)),
    },
    current: currentState(proxy),
    notes:
      'Text is sanitized and truncated to max_characters_per_line automatically. Photo-type labels require a shopper-uploaded photo and cannot be completed by this tool.',
  };
};

/**
 * Applies personalization input to the mounted customizer via the same
 * reactive state the shopper's own inputs drive, then waits for Vue to flush
 * its watchers so sanitization, truncation, and the canvas re-render have
 * happened before the caller reads state or adds to cart.
 *
 * @param {object} proxy
 * @param {object} input
 * @returns {Promise<{problems: string[], warnings: string[]}>} problems block an add; warnings do not
 */
const applyPersonalization = async (proxy, input) => {
  const problems = [];
  const warnings = [];

  const selections = [
    ['font', proxy.options?.fonts, 'font'],
    ['color', proxy.options?.colors, 'color'],
    ['color_alt', proxy.options?.color_alts, 'color_alt'],
    ['icon_set', proxy.options?.sets, 'set'],
  ];

  for (const [inputKey, list, selectionKey] of selections) {
    const value = input[inputKey];
    if (!value) continue;

    const option = findOption(list, value);
    if (!option) {
      problems.push(`Unknown ${inputKey} "${value}". Available: ${optionNames(list).join(', ') || 'none'}.`);
      continue;
    }
    proxy.selection[selectionKey] = option;
  }

  // letter first, so an explicit orbit_icon below wins when both are given.
  if (input.letter) proxy.texts.Letter = input.letter;

  if (input.orbit_icon) {
    const icons = orbitIconList(proxy);
    const icon = findOption(icons, input.orbit_icon);
    if (icon) {
      proxy.texts.Letter = icon.key;
      if (input.letter) warnings.push('Both letter and orbit_icon were given; orbit_icon was used.');
    } else {
      problems.push(`Unknown orbit_icon "${input.orbit_icon}". Available: ${optionNames(icons).join(', ') || 'none'}.`);
    }
  }

  if (input.line_1 != null) proxy.texts.Line_1 = String(input.line_1);

  if (input.line_2 != null) {
    if (allowsLine2(proxy)) {
      proxy.texts.Line_2 = String(input.line_2);
    } else {
      warnings.push('line_2 ignored: this label is single-line (or the selected font does not support a second line).');
    }
  }

  if (input.quantity != null && 'quantity' in proxy) {
    proxy.quantity = Math.max(1, Number(input.quantity) || 1);
  }

  // Vue's texts watcher (sanitize + truncate + canvas re-render) flushes
  // asynchronously; without this wait the canvas capture and the property
  // values read by addToCart would be stale.
  if (typeof proxy.$nextTick === 'function') await proxy.$nextTick();
  await new Promise((resolve) => setTimeout(resolve, 50));

  return { problems, warnings };
};

/**
 * Waits for the customizer's add-to-cart flow (image upload + /cart/add.js +
 * Cart Transform nudge) to settle by polling its loading flag.
 *
 * @param {object} proxy
 * @returns {Promise<void>}
 */
const waitForCartFlow = async (proxy) => {
  const started = Date.now();

  // Give the flow a beat to flip loading on before polling it off.
  await new Promise((resolve) => setTimeout(resolve, 500));

  while (proxy.loading && Date.now() - started < TOOL_TIMEOUT_MS) {
    await new Promise((resolve) => setTimeout(resolve, 250));
  }
};

const fetchCartSummary = async () => {
  const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
  if (!response.ok) return null;

  const cart = await response.json();
  return {
    item_count: cart.item_count,
    items: cart.items.slice(0, 10).map((item) => ({
      title: item.product_title,
      quantity: item.quantity,
      properties: Object.fromEntries(Object.entries(item.properties ?? {}).filter(([key]) => !key.startsWith('_'))),
    })),
  };
};

/** @type {Promise<object | null> | null} */
let quizDataPromise = null;

/**
 * Fetches the quiz mapping through the Section Rendering API, once, on the
 * first recommendation call — regular page loads never carry this payload.
 *
 * @returns {Promise<object | null>}
 */
const getQuizData = () => {
  quizDataPromise ??= (async () => {
    try {
      const response = await fetch(`/?sections=${QUIZ_DATA_SECTION}`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return null;

      const sections = await response.json();
      const html = sections[QUIZ_DATA_SECTION];
      if (!html) return null;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const blob = doc.querySelector('[data-webmcp-quiz]');
      if (!blob) return null;

      const quiz = JSON.parse(blob.textContent);
      return quiz?.questions?.length ? quiz : null;
    } catch (error) {
      console.error('[webmcp] quiz data fetch failed', error);
      return null;
    }
  })();

  return quizDataPromise;
};

const personalizationInputSchema = {
  type: 'object',
  properties: {
    line_1: { type: 'string', description: 'Main personalization text, usually the child’s first name.' },
    line_2: {
      type: 'string',
      description: 'Optional second line (for example a last name). Only applied when the label supports two lines.',
    },
    letter: { type: 'string', description: 'Single letter for monogram labels.' },
    font: { type: 'string', description: 'Font name from inchbug_get_personalization_options.' },
    color: { type: 'string', description: 'Color name from inchbug_get_personalization_options.' },
    color_alt: { type: 'string', description: 'Secondary color name, when the product offers one.' },
    icon_set: { type: 'string', description: 'Design / icon set name from inchbug_get_personalization_options.' },
    orbit_icon: { type: 'string', description: 'Icon name for Orbit label icon sets.' },
    quantity: { type: 'number', description: 'Quantity to add. Defaults to 1.' },
  },
};

const registerLocalTool = (tool) => {
  localTools.set(tool.name, tool);

  const modelContext = document.modelContext ?? navigator.modelContext;
  if (!modelContext?.registerTool) return;

  const descriptor = {
    ...tool,
    execute: async (input, extras) => {
      track(tool.name, input);
      return tool.execute(input ?? {}, extras ?? {});
    },
  };

  try {
    const result = modelContext.registerTool(descriptor);
    if (result?.catch) result.catch((error) => console.error(`[webmcp] failed to register ${tool.name}`, error));
  } catch (error) {
    console.error(`[webmcp] failed to register ${tool.name}`, error);
  }
};

registerLocalTool({
  name: 'inchbug_get_personalization_options',
  description:
    'Reads the personalization options and constraints for the InchBug label product open on this page: available fonts, colors, designs, how many text lines the label supports, and the max characters per line. Call this before previewing or adding a personalized label so you only use valid values. If no personalizable product is open it explains how to reach one.',
  inputSchema: { type: 'object', properties: {} },
  annotations: { readOnlyHint: true },
  execute: async () => {
    const customizer = getCustomizer();
    if (!customizer) return noCustomizerMessage();

    return JSON.stringify(describeCustomizer(customizer.proxy));
  },
});

registerLocalTool({
  name: 'inchbug_preview_personalization',
  description:
    'Applies personalization (name text, font, color, design) to the live label preview on this page so the shopper can see their label rendered before buying. Does NOT add to cart. Requires a personalizable product page to be open — use inchbug_get_personalization_options first to see valid values.',
  inputSchema: personalizationInputSchema,
  execute: async (input) => {
    const customizer = getCustomizer();
    if (!customizer) return noCustomizerMessage();

    const { problems, warnings } = await applyPersonalization(customizer.proxy, input);

    return JSON.stringify({
      applied: problems.length === 0,
      problems: [...problems, ...warnings],
      state: currentState(customizer.proxy),
    });
  },
});

registerLocalTool({
  name: 'inchbug_add_personalized_to_cart',
  description:
    'Adds the personalized label product on this page to the cart WITH its personalization (name, font, color, design) attached as line item properties — the generic update_cart tool cannot do this and would add a blank, un-personalized label. Provide the personalization fields (or preview first), and this tool validates, renders the label image, and adds it through the store’s own cart flow. Confirm the personalization details with the shopper before calling.',
  inputSchema: personalizationInputSchema,
  execute: async (input) => {
    const customizer = getCustomizer();
    if (!customizer) return noCustomizerMessage();

    const { proxy } = customizer;
    const { problems, warnings } = await applyPersonalization(proxy, input);
    if (problems.length) return JSON.stringify({ added: false, problems: [...problems, ...warnings] });

    if (proxy.label?.type === 'photo' && !proxy.photo?.upload) {
      return JSON.stringify({
        added: false,
        problems: ['This is a photo label: the shopper must upload a photo on the page before it can be added to cart.'],
      });
    }

    if (!proxy.isCustomizationComplete) {
      return JSON.stringify({
        added: false,
        problems: ['Personalization is incomplete. At minimum line_1 (or letter for monogram labels) is required.'],
        state: currentState(proxy),
      });
    }

    if (proxy.loading) return JSON.stringify({ added: false, problems: ['The customizer is busy. Try again shortly.'] });

    const cartBefore = await fetchCartSummary();

    proxy.isConfirmed = true;
    proxy.preAddToCart();
    await waitForCartFlow(proxy);

    const cart = await fetchCartSummary();
    const added = Boolean(cart && (!cartBefore || cart.item_count > cartBefore.item_count));

    return JSON.stringify({
      added,
      ...(added
        ? {}
        : {
            problems: [
              'The add did not complete — the cart is unchanged. The item may be sold out, a bundle step may be incomplete, or the label image upload failed. Check the page for an error message.',
            ],
          }),
      warnings,
      personalization: currentState(proxy),
      cart,
    });
  },
});

registerLocalTool({
  name: 'inchbug_recommend_labels',
  description:
    'Recommends the best InchBug label products for a child, using the same scoring as the on-site product quiz. Provide the child’s age group, where their belongings go, and/or what needs labeling. Works on any page. Returns ranked products with URLs — personalizable ones can then be opened and customized with the inchbug personalization tools.',
  inputSchema: {
    type: 'object',
    properties: {
      age: {
        type: 'string',
        enum: ['baby', 'toddler', 'preschool', 'elementary'],
        description: 'Child’s age group.',
      },
      destination: {
        type: 'string',
        enum: ['daycare', 'school', 'camp', 'sports', 'everywhere'],
        description: 'Where the labeled belongings mostly go.',
      },
      items: {
        type: 'array',
        items: { type: 'string', enum: ['clothes', 'shoes', 'lunch', 'backpacks', 'all'] },
        description: 'What needs a name on it.',
      },
      limit: { type: 'number', description: 'Max products to return, 1-50. Defaults to 8.' },
    },
  },
  annotations: { readOnlyHint: true },
  execute: async (input) => {
    const wanted = new Set(
      [
        input.age ? `age-${input.age}` : null,
        input.destination ? `destination-${input.destination}` : null,
        ...(input.items ?? []).map((item) => `item-${item}`),
      ].filter(Boolean)
    );

    if (!wanted.size) {
      return JSON.stringify({
        error: 'no_input',
        message: 'Provide at least one of age, destination, or items.',
      });
    }

    const quiz = await getQuizData();
    if (!quiz) {
      return JSON.stringify({
        error: 'quiz_unavailable',
        message: 'The recommendation data could not be loaded. Fall back to search_catalog.',
      });
    }

    const selections = new Map();
    for (const question of quiz.questions) {
      const matches = question.answers.map((answer) => answer.handle).filter((handle) => wanted.has(handle));
      if (matches.length) selections.set(question.handle, matches);
    }

    if (!selections.size) {
      // Guards against silent drift if quiz answer handles are renamed in
      // the admin — an empty result must not read as "no products".
      return JSON.stringify({
        error: 'no_matching_answers',
        message: `The provided values matched no quiz answers. Known answer handles: ${quiz.questions
          .flatMap((question) => question.answers.map((answer) => answer.handle))
          .join(', ')}.`,
      });
    }

    const limit = Math.max(1, Math.min(50, Number(input.limit) || 8));
    const { rankQuizProducts } = await import('@theme/metaobject-product-quiz');
    const ranked = rankQuizProducts(quiz, selections, limit);

    return JSON.stringify({
      products: ranked.map(({ title, handle, url, price, score }) => ({
        title,
        handle,
        url: new URL(url, window.location.origin).toString(),
        price,
        score,
      })),
    });
  },
});

// Console test harness — lets anyone exercise the tools from DevTools without
// a WebMCP-capable agent: window.inchbugWebMCP.call('inchbug_recommend_labels', { age: 'toddler' })
// Harness calls deliberately do NOT dispatch the analytics event, so manual
// testing never masquerades as agent traffic.
window.inchbugWebMCP = {
  get tools() {
    return [...localTools.keys()];
  },
  call: async (name, input = {}) => {
    const tool = localTools.get(name);
    if (!tool) throw new Error(`Unknown tool "${name}". Registered: ${[...localTools.keys()].join(', ')}`);
    return tool.execute(input, {});
  },
  supported: Boolean((document.modelContext ?? navigator.modelContext)?.registerTool),
};
